import { GameEngine } from '../game/GameEngine.ts';
import { DEFAULT_CONFIG, type GameConfig, type GameState } from '../game/GameState.ts';
import type { SessionExport } from './EventLogger.ts';
import { EventType, type ExperimentEvent } from './Events.ts';

/**
 * One run within a session: everything between a GAME_RESET and the next one.
 */
export interface ReplaySegment {
  index: number;
  seed: number;
  /** Ticks on which a flap was actually executed. This is the whole input. */
  flapTicks: number[];
  /** Events belonging to this run, in order. */
  events: ExperimentEvent[];
  /** Recorded outcome, used to verify the replay reproduces the original. */
  recordedScore: number | null;
  recordedFinalTick: number | null;
  trialId?: string;
}

export interface ReplayVerification {
  segment: number;
  ok: boolean;
  recordedScore: number | null;
  replayedScore: number;
  recordedFinalTick: number | null;
  replayedFinalTick: number;
  note: string;
}

/**
 * Deterministic session replay.
 *
 * The whole thing rests on one property of `GameEngine`: given a seed and the
 * sequence of ticks on which a flap was executed, the simulation is
 * reproducible bit for bit. So a replay does not need recorded positions --
 * it needs a seed and a list of integers, and it regenerates everything else.
 *
 * That is also a useful integrity check. `verify()` re-runs each segment and
 * compares the reconstructed score and death tick against what was logged. If
 * they disagree, something non-deterministic has crept into the engine, and
 * every comparison in the study is suspect until it is found.
 *
 * Playback speed is decoupled from the simulation rate: `advance()` consumes
 * real elapsed time, multiplies it by `speed`, and steps the fixed timestep as
 * many times as that buys. At 0.1x the bird crawls and the tick counter still
 * advances one tick at a time, which is what makes it possible to see the four
 * frames in which a stimulation request was too late.
 */
export class Replay {
  readonly config: GameConfig;
  readonly segments: ReplaySegment[];

  private engine: GameEngine;
  private segmentIndex = 0;
  private flapSet = new Set<number>();
  private accumulatorMs = 0;
  private lastAdvanceAt: number | null = null;

  /** Playback rate. 1 = realtime, 0.1 = slow motion, 4 = fast scan. */
  speed = 1;
  playing = false;

  constructor(config: Partial<GameConfig>, segments: ReplaySegment[]) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.segments = segments;
    this.engine = new GameEngine(this.config);
    // Replay must not read the wall clock: a replayed state's `timestamp`
    // is meaningless, and using the real one would make it look like data.
    this.engine.now = () => this.engine.simTimeMs;
    this.selectSegment(0);
  }

  /**
   * Build a replay from an exported session.
   *
   * Accepts the object form or a JSON string, so a researcher can paste a file
   * straight in.
   */
  static fromExport(data: SessionExport | string): Replay {
    const parsed: SessionExport = typeof data === 'string' ? JSON.parse(data) : data;
    if (parsed?.format !== 'flappy-hhi/session') {
      throw new Error('Not a flappy-hhi session export');
    }
    const config = (parsed.session.gameConfig as Partial<GameConfig>) ?? {};
    return new Replay(config, Replay.segmentize(parsed.events, config.seed ?? DEFAULT_CONFIG.seed));
  }

  /**
   * Split a flat event list into runs.
   *
   * Ticks restart at zero on every GAME_RESET, so segmentation has to follow
   * event order rather than tick order -- a subtlety worth stating, because
   * getting it wrong silently interleaves two runs into one nonsensical replay.
   */
  static segmentize(events: readonly ExperimentEvent[], defaultSeed: number): ReplaySegment[] {
    const segments: ReplaySegment[] = [];
    let current: ReplaySegment | null = null;

    const begin = (seed: number, trialId?: string) => {
      current = {
        index: segments.length,
        seed,
        flapTicks: [],
        events: [],
        recordedScore: null,
        recordedFinalTick: null,
        trialId,
      };
      segments.push(current);
    };

    for (const e of events) {
      if (e.type === EventType.GAME_RESET) {
        begin(typeof e.metadata?.seed === 'number' ? e.metadata.seed : defaultSeed, e.trialId);
        continue;
      }
      if (!current) begin(defaultSeed, e.trialId);
      const seg = segments[segments.length - 1];
      seg.events.push(e);
      if (e.type === EventType.FLAP_EXECUTED) seg.flapTicks.push(e.tick);
      if (e.type === EventType.SCORE && typeof e.metadata?.score === 'number') {
        seg.recordedScore = e.metadata.score;
      }
      if (e.type === EventType.GAME_OVER) {
        seg.recordedFinalTick = e.tick;
        if (typeof e.metadata?.score === 'number') seg.recordedScore = e.metadata.score;
      }
    }
    return segments.filter((s) => s.events.length > 0 || s.flapTicks.length > 0);
  }

  // ------------------------------------------------------------------ control

  get segment(): ReplaySegment | undefined {
    return this.segments[this.segmentIndex];
  }

  get currentIndex(): number {
    return this.segmentIndex;
  }

  /** Last tick with recorded activity; the end of the scrub bar. */
  get lengthTicks(): number {
    const seg = this.segment;
    if (!seg) return 0;
    const lastFlap = seg.flapTicks[seg.flapTicks.length - 1] ?? 0;
    const lastEvent = seg.events[seg.events.length - 1]?.tick ?? 0;
    return Math.max(seg.recordedFinalTick ?? 0, lastFlap, lastEvent);
  }

  selectSegment(index: number): void {
    this.segmentIndex = Math.max(0, Math.min(index, Math.max(0, this.segments.length - 1)));
    const seg = this.segment;
    this.flapSet = new Set(seg?.flapTicks ?? []);
    this.engine.reset(seg?.seed ?? this.config.seed);
    this.accumulatorMs = 0;
    this.lastAdvanceAt = null;
  }

  restart(): void {
    this.selectSegment(this.segmentIndex);
  }

  /** Re-simulate from the start up to `tick`. Cheap: ~10^4 steps at worst. */
  seek(tick: number): GameState {
    const seg = this.segment;
    this.engine.reset(seg?.seed ?? this.config.seed);
    for (let t = 0; t < tick; t++) {
      this.engine.step(this.flapSet.has(this.engine.currentTick));
    }
    return this.engine.getState();
  }

  step(): GameState {
    return this.engine.step(this.flapSet.has(this.engine.currentTick));
  }

  /** Drive playback from a real-time clock. @returns ticks stepped. */
  advance(nowMs: number): number {
    if (!this.playing) {
      this.lastAdvanceAt = nowMs;
      return 0;
    }
    if (this.lastAdvanceAt === null) {
      this.lastAdvanceAt = nowMs;
      return 0;
    }
    const dtMs = 1000 / this.config.simHz;
    const elapsed = Math.min(nowMs - this.lastAdvanceAt, 250) * this.speed;
    this.lastAdvanceAt = nowMs;
    this.accumulatorMs += elapsed;

    let steps = 0;
    while (this.accumulatorMs >= dtMs) {
      this.accumulatorMs -= dtMs;
      if (this.engine.currentTick > this.lengthTicks) {
        this.playing = false;
        break;
      }
      this.step();
      steps++;
    }
    return steps;
  }

  getState(): GameState {
    return this.engine.getState();
  }

  /** Events recorded on a given tick of the current segment. */
  eventsAt(tick: number): ExperimentEvent[] {
    return this.segment?.events.filter((e) => e.tick === tick) ?? [];
  }

  // ------------------------------------------------------------ verification

  /** Re-run every segment and check it reproduces what was logged. */
  verify(): ReplayVerification[] {
    const saved = this.segmentIndex;
    const out: ReplayVerification[] = [];
    for (let i = 0; i < this.segments.length; i++) {
      const seg = this.segments[i];
      const engine = new GameEngine({ ...this.config, seed: seg.seed });
      engine.now = () => engine.simTimeMs;
      const flaps = new Set(seg.flapTicks);
      const limit = Math.max(this.lengthTicksOf(seg) + 2, 1);
      while (engine.isAlive && engine.currentTick <= limit) {
        engine.step(flaps.has(engine.currentTick));
      }
      const replayedScore = engine.getState().score;
      const replayedFinalTick = engine.currentTick;
      const scoreOk = seg.recordedScore === null || seg.recordedScore === replayedScore;
      const tickOk =
        seg.recordedFinalTick === null || Math.abs(seg.recordedFinalTick - replayedFinalTick) <= 1;
      out.push({
        segment: i,
        ok: scoreOk && tickOk,
        recordedScore: seg.recordedScore,
        replayedScore,
        recordedFinalTick: seg.recordedFinalTick,
        replayedFinalTick,
        note:
          scoreOk && tickOk
            ? 'reproduced exactly'
            : 'MISMATCH — the engine or the log is not deterministic',
      });
    }
    this.selectSegment(saved);
    return out;
  }

  private lengthTicksOf(seg: ReplaySegment): number {
    const lastFlap = seg.flapTicks[seg.flapTicks.length - 1] ?? 0;
    const lastEvent = seg.events[seg.events.length - 1]?.tick ?? 0;
    return Math.max(seg.recordedFinalTick ?? 0, lastFlap, lastEvent);
  }
}
